var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import * as _ from "lodash";
import moment from "moment";
const _CommonUtils = class _CommonUtils {
  constructor() {
    __publicField(this, "maxDate", /* @__PURE__ */ new Date(864e13));
    __publicField(this, "minDate", /* @__PURE__ */ new Date(-864e13));
  }
  /**
   * Formats a number with its SI suffix (e.g. 10000 -> 10k).
   * @see https://gist.github.com/cho45/9968462
   *
   * @param num The number to format
   * @param decimals The number of decimals to show ((1234, 2) => '1.23k')
   * @returns The suffixed string
   */
  formatIntSiSuffixed(number, decimals = 1) {
    if (number === 0)
      return number.toString();
    const EXP_STEP_SIZE = 3;
    const base = Math.floor(Math.log10(Math.abs(number)));
    const siBase = (base < 0 ? Math.ceil : Math.floor)(base / EXP_STEP_SIZE);
    const prefix = _CommonUtils.SI_PREFIXES[siBase + _CommonUtils.SI_PREFIXES_CENTER_INDEX];
    if (siBase === 0)
      return number.toString();
    const baseNumber = parseFloat((number / Math.pow(10, siBase * EXP_STEP_SIZE)).toFixed(decimals));
    return `${baseNumber}${prefix}`;
  }
  belongsTo(obj, arr) {
    return arr.indexOf(obj) >= 0;
  }
  changePropertyNameInDictionary(dict, oldKey, newKey) {
    dict[newKey] = dict[oldKey];
    delete dict[oldKey];
  }
  getMatchingKeyIgnoreCase(dict, compareKey) {
    if (!dict || !compareKey) {
      return null;
    }
    let keys = Object.keys(dict);
    let match = keys.filter((key) => compareIgnoreCase(key, compareKey));
    if (!!match) {
      return match[0];
    }
    return null;
  }
  getMatchingValueIgnoreCase(dict, compareKey) {
    if (!dict || !compareKey) {
      return null;
    }
    let key = this.getMatchingKeyIgnoreCase(dict, compareKey);
    if (!!key) {
      return dict[key];
    }
    return null;
  }
  /**
   * Small utility function to correct casing of the dictionary key based on a source key
   * @param dict : to be corrected
   * @param sourceDictionary : source of truth dictionary.
   */
  correctCasingForKeys(dict, sourceDictionary) {
    let currentKeys = Object.keys(dict);
    _.forEach(currentKeys, (key) => {
      let sourceKey = this.getMatchingKeyIgnoreCase(sourceDictionary, key);
      if (!!sourceKey && key !== sourceKey) {
        this.changePropertyNameInDictionary(dict, key, sourceKey);
      }
    });
  }
  hasDuplicate(array) {
    if (!array) {
      return false;
    }
    array = array.filter((a) => a !== null);
    return new Set(array).size !== array.length;
  }
  LeftMergeDictionary(a, b) {
    const result = {};
    _.forOwn(b, (item, key) => {
      result[key] = item;
    });
    _.forOwn(a, (item, key) => {
      result[key] = item;
    });
    return result;
  }
  /**
   * Compare if 2 arrays have same element , (may be in different order)
   * @param arr1
   * @param arr2
   * @param compareFn : Sorting function for ordering
   * @param equalFn: Equality Comparsion for 2 elements in different array
   */
  IsArrayEqualIgnoreOrder(arr1, arr2, compareFn, equalFn) {
    if (this.IsNullOrEmpty(arr1) && this.IsNullOrEmpty(arr2)) {
      return true;
    }
    if (this.IsNullOrEmpty(arr1) || this.IsNullOrEmpty(arr2)) {
      return false;
    }
    if (arr1.length !== arr2.length) {
      return false;
    }
    arr1 = _.cloneDeep(arr1);
    arr2 = _.cloneDeep(arr2);
    arr1.sort(!!compareFn ? (p, q) => compareFn(p, q) : void 0);
    arr2.sort(!!compareFn ? (p, q) => compareFn(p, q) : void 0);
    return this.IsArrayEqual(arr1, arr2, equalFn);
  }
  IsArrayEqual(arr1, arr2, equalFn) {
    if (!arr1 && !arr2) {
      return true;
    }
    if (!arr1 || !arr2) {
      return false;
    }
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (!_.isEqualWith(arr1[i], arr2[i], (p, q) => !!equalFn ? equalFn(p, q) : void 0)) {
        return false;
      }
    }
    return true;
  }
  IsNullOrEmpty(arr) {
    return !arr || arr.length === 0;
  }
  sortByDateString(objects, dateProperty, asc = true) {
    const defaultDate = asc ? this.maxDate : this.minDate;
    return objects.sort((p1, p2) => {
      let t1 = p1[dateProperty] ? new Date(p1[dateProperty]) : defaultDate;
      let t2 = p2[dateProperty] ? new Date(p2[dateProperty]) : defaultDate;
      return asc ? t1 - t2 : t2 - t1;
    });
  }
  getPropName(obj, value) {
    if (!!obj) {
      for (let prop in obj) {
        if (!!obj[prop] && obj[prop] === value) {
          return prop;
        }
      }
    }
    return "";
  }
  removeAt(arr, ignoreIndices) {
    arr = _.cloneDeep(arr);
    for (let i = ignoreIndices.length - 1; i >= 0; --i) {
      arr.splice(ignoreIndices[i], 1);
    }
    return arr;
  }
  getMax(arr) {
    if (!arr || arr.filter((n) => !!n).length === 0) {
      return NaN;
    }
    return _.max(arr.filter((n) => !!n));
  }
  getValues(dict) {
    if (!dict) {
      return [];
    }
    return _.map(dict, (value) => value);
  }
  contains(arr, val) {
    if (!arr || arr.length === 0) {
      return false;
    }
    return arr.filter((a) => a === val).length > 0;
  }
  roundTo(input, decimalPlaces) {
    if (!input) {
      return 0;
    }
    if (!decimalPlaces) {
      return input;
    }
    return parseFloat(input.toFixed(decimalPlaces));
  }
  roundStringToNumber(input, decimalPlaces) {
    if (!input) {
      return 0;
    }
    return this.roundTo(Number.parseFloat(input), decimalPlaces);
  }
  getEnumIgnoreCase(enumObject, input, defaultValue) {
    if (!enumObject || !input) {
      return defaultValue;
    }
    for (const p in enumObject) {
      if (p.toLocaleLowerCase() === input.toLocaleLowerCase()) {
        return enumObject[p.toString()];
      }
    }
    return defaultValue;
  }
  copyToClipboard(text) {
    let el = document.createElement("textarea");
    document.body.appendChild(el);
    el.value = text;
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
  removeQueryParamFromURL(href, queryParam) {
    if (!href) {
      return "";
    }
    let url = new URL(href);
    url.search = this.removeQueryParamFromString(url.search, queryParam);
    return url.toString();
  }
  setQueryParamInURL(href, queryParam, queryParamValue) {
    if (!href || !queryParam) {
      return "";
    }
    let url = new URL(href);
    url.search = this.setQueryParam(url.search, queryParam, queryParamValue);
    return url.toString();
  }
  limitDecimalToSingleDigit(d) {
    d = d.replace(/[^\d.]/g, "");
    const n = Number.parseFloat(d);
    if (isNaN(n)) {
      return "";
    }
    let factor = 1;
    let trunced = 0;
    while (trunced === 0 && n > 0) {
      factor *= 10;
      trunced = Math.floor(n * factor) / factor;
    }
    return Number(trunced.toFixed(1)).toString() + (d.slice(-1) === "." ? "." : "");
  }
  removeQueryParamFromString(search, queryParam) {
    if (!queryParam) {
      return search;
    }
    let queryParams = new URLSearchParams(search);
    queryParams.delete(queryParam);
    return queryParams.toString();
  }
  setQueryParam(search, queryParam, queryParamValue) {
    if (!queryParam) {
      return search;
    }
    let queryParams = new URLSearchParams(search);
    queryParams.set(queryParam, queryParamValue);
    return queryParams.toString();
  }
  dateDifferenceInString(date1, date2) {
    if (!date1 || !date2) {
      return "";
    }
    let result = "";
    const dateDifference = this.dateDifference(date1, date2);
    const keys = Object.keys(dateDifference);
    let key = "";
    for (let i = 0; i < keys.length; i++) {
      key = keys[i];
      if (dateDifference[key] > 0) {
        if (dateDifference[key] > 1) {
          result = `${dateDifference[key]} ${key}s`;
        } else {
          result = `${dateDifference[key]} ${key}`;
        }
        break;
      }
    }
    return result;
  }
  dateDifference(date1, date2) {
    const result = {
      year: 0,
      month: 0,
      week: 0,
      day: 0,
      hour: 0,
      minute: 0,
      second: 0
    };
    if (!date1 || !date2) {
      return result;
    }
    const moment1 = moment(date1);
    const moment2 = moment(date2);
    return {
      year: Math.abs(moment1.diff(moment2, "years")),
      month: Math.abs(moment1.diff(moment2, "months")),
      week: Math.abs(moment1.diff(moment2, "weeks")),
      day: Math.abs(moment1.diff(moment2, "days")),
      hour: Math.abs(moment1.diff(moment2, "hours")),
      minute: Math.abs(moment1.diff(moment2, "minutes")),
      second: Math.abs(moment1.diff(moment2, "seconds"))
    };
  }
  divide(num, den, decimalPlaces) {
    if (!den || !num) {
      return "0";
    }
    return (num / den).toFixed(decimalPlaces);
  }
  /**
   * Time to show the time interval from event happen time to current time, or event happen timestamp with a specific format
   * a.If < 24h before current time, show event happen time to current time(e.g. xxx seconds/minutes/hours ago).
   * b.If > 24h bofore current time, the DateTime format(e.g. 01/03/2020 05:00:00 PM (UTC)).
   * @param eventHappenTime
   * @param timeFormat
   * @param currentTime
   * @returns
   */
  convertTime(eventHappenTime, timeFormat = "M/D/YY HH:mm:ss", currentTime) {
    const current = currentTime ? moment(moment(currentTime).format(timeFormat)) : moment(moment().utc().format(timeFormat));
    const eventHappen = moment(moment(eventHappenTime));
    const dayDiff = Math.floor(moment.duration(current.diff(eventHappen)).asDays());
    if (dayDiff >= 1) {
      return moment(eventHappenTime).format(timeFormat) + " (UTC)";
    } else {
      const hourDiff = Math.floor(moment.duration(current.diff(eventHappen)).asHours());
      if (hourDiff >= 1) {
        return hourDiff + (hourDiff === 1 ? " hour ago" : " hours ago");
      } else {
        const minuteDiff = Math.floor(moment.duration(current.diff(eventHappen)).asMinutes());
        if (minuteDiff >= 1) {
          return minuteDiff + (minuteDiff === 1 ? " minute ago" : " minutes ago");
        } else {
          const secondDiff = Math.floor(moment.duration(current.diff(eventHappen)).asSeconds());
          if (secondDiff >= 1) {
            return secondDiff + (secondDiff === 1 ? " second ago" : " seconds ago");
          }
          return moment(eventHappenTime).format(timeFormat) + " (UTC)";
        }
      }
    }
  }
  /**
   * check if current page is on the selected tab of the browser
   * @param debugging
   * @returns
   */
  isPageVisible(debugging) {
    if (debugging) {
      return true;
    }
    return document.visibilityState === "visible";
  }
};
__publicField(_CommonUtils, "SI_PREFIXES_CENTER_INDEX", 8);
__publicField(_CommonUtils, "SI_PREFIXES", [
  "y",
  "z",
  "a",
  "f",
  "p",
  "n",
  "μ",
  "m",
  "",
  "k",
  "M",
  "G",
  "T",
  "P",
  "E",
  "Z",
  "Y"
]);
let CommonUtils = _CommonUtils;
const CommonUtilsInstance = new CommonUtils();
const strEndsWith = (str, target) => {
  return str.substr(-target.length) === target;
};
const getNewGuid = () => {
  function s4() {
    return Math.floor((1 + Math.random()) * 65536).toString(16).substring(1);
  }
  return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
};
const isGuid = (s) => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
};
const compareIgnoreCase = (a, b) => {
  if (a === b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return a.toLowerCase() === b.toLowerCase();
  }
};
const includesIgnoreCase = (a, b) => {
  if (a === b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    const a_lower = a.toLowerCase();
    const b_lower = b.toLowerCase();
    return a_lower.includes(b_lower);
  }
};
const toLowerCaseArray = (arr) => {
  if (!arr || arr.length === 0) {
    return [];
  }
  return _.map(arr, (element) => element.toLowerCase());
};
const removeEmptyValues = (arr) => {
  if (!arr || arr.length === 0) {
    return [];
  }
  return arr.filter((element) => !!element);
};
const joinString = (input) => {
  if (!input || input.length === 0) {
    return "";
  }
  if (input.length === 1) {
    const last2 = input.pop();
    return !!last2 ? last2 : "";
  }
  const last = input.pop();
  return input.join(", ") + " and " + last;
};
const toLowerCaseDictionary = (dict) => {
  if (!dict) {
    return {};
  }
  let currentKeys = Object.keys(dict);
  _.forEach(currentKeys, (key) => {
    let lowerKey = key.toLowerCase();
    if (!!lowerKey && key !== lowerKey) {
      CommonUtilsInstance.changePropertyNameInDictionary(dict, key, lowerKey);
    }
  });
  return dict;
};
const containsIgnoreCase = (arr, b) => {
  return !!arr && _.some(arr, (s) => {
    return compareIgnoreCase(s, b);
  });
};
const friendlyAssetName = (name) => {
  if (!name) {
    return null;
  }
  name = name.replace(/[^a-zA-Z0-9-_\/\.]/g, "_");
  name = name.replace(/[\.]{2,}/g, ".");
  return name;
};
const removeSpecialCharacters = (name) => {
  if (!name) {
    return "";
  }
  name = name.replace(/[^a-zA-Z0-9 _-]/g, "");
  return name;
};
const hashCode = (str) => {
  var _a;
  return !!str ? (_a = Array.from(str).reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0)) == null ? void 0 : _a.toString() : "";
};
const getArticleText = (name) => {
  return name.match("^[aieouAIEOU].*") ? "an" : "a";
};
class CommonStringUtils {
  markdownListOfString(title, listOfString) {
    let message = _.map(listOfString, (str) => `* ${str}`).join(" ");
    return `${title}
        ${message}`;
  }
}
const CommonStringUtilsInstance = new CommonStringUtils();
export {
  CommonUtilsInstance as C,
  CommonUtils as a,
  includesIgnoreCase as b,
  compareIgnoreCase as c,
  toLowerCaseDictionary as d,
  containsIgnoreCase as e,
  friendlyAssetName as f,
  getNewGuid as g,
  removeSpecialCharacters as h,
  isGuid as i,
  joinString as j,
  hashCode as k,
  getArticleText as l,
  CommonStringUtils as m,
  CommonStringUtilsInstance as n,
  removeEmptyValues as r,
  strEndsWith as s,
  toLowerCaseArray as t
};
//# sourceMappingURL=CommonStringUtils-8860a016.js.map
